// Generated by tutorials/record.cjs from tutorials/articles/h05-ehr-frequencies-action-plans.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h05 --text-only` (wording) or
// `node tutorials/record.cjs h05` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'ehr-frequencies-action-plans',
  catalogId: 'h05',
  title: 'EHR frequencies and action plans',
  summary: 'Tell Dietech what "daily", "BID" and the other order frequencies mean at your facility, so supplements from your EHR land at the right mealtimes, and map the EHR text that adds a problem, goal and approach to a resident\'s plan of care.',
  audience: 'Dietary managers and dietitians who look after the orders your EHR sends',
  area: 'residents',
  learn: [
    'See what your EHR may give residents at your facility',
    'Set, change, clear and add frequencies such as daily, BID and HS',
    'How a supplement order picks its mealtimes from the frequency',
    'Map EHR text to an action plan',
    'What an action plan adds to a resident, and when it is resolved',
  ],
  keywords: [
    'ehr',
    'emr',
    'frequency',
    'frequencies',
    'daily',
    'bid',
    'tid',
    'qid',
    'hs',
    'mealtimes',
    'supplement',
    'action plan',
    'action plans',
    'problem',
    'goal',
    'approach',
    'plan of care',
    'problem template',
    'mappings',
  ],
  steps: [
    {
      id: 'switches',
      part: 'Set what each frequency means',
      heading: 'Open Frequencies',
      text: 'On the EHR page, select Mappings, then Frequencies.\n\nThe top shows what your EHR may give residents here: supplements, tube feeds, global adjustments and meal patterns, and action plans. Dietech support turns each one on or off for your facility.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/switches.webp',
          width: 1280,
          height: 208,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/switches.mp4',
          width: 1280,
          height: 208,
          durationSec: 4.6,
        },
        alt: 'What the EHR may give here: Supplements On, Tube feeds On, Global adjustments and meal patterns Off, Action plans On.',
      },
    },
    {
      id: 'table',
      part: 'Set what each frequency means',
      heading: 'What each frequency means here',
      text: 'Frequencies lists each frequency an order can have and the mealtimes it means at your facility. A supplement ordered "BID" is given at the mealtimes set for BID, here Lunch and Dinner.\n\nNothing is filled in for you. DAILY is Not set, so supplement text with it waits to be mapped: Waiting says how much, and for how many residents.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/table.webp',
          width: 1280,
          height: 466,
        },
        alt: 'The Frequencies table: DAILY Not set with 3 phrases for 2 residents waiting, BID Lunch, Dinner, TID Breakfast, Lunch, Dinner, QID Not set, HS HS Snack and MEALS Breakfast, Lunch, Dinner.',
      },
    },
    {
      id: 'set-daily',
      part: 'Set what each frequency means',
      heading: 'Set a frequency',
      text: 'Select Set beside DAILY, check the mealtime "daily" means at your facility, then select Save.\n\nThe residents already waiting stay listed until their next message from your EHR, which then gives the supplement at that mealtime.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/set-daily.webp',
          width: 1280,
          height: 466,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/set-daily.mp4',
          width: 1280,
          height: 466,
          durationSec: 6,
        },
        alt: 'DAILY now means Lunch. Its Waiting column says 2 residents are still listed and clear on their next message.',
      },
    },
    {
      id: 'change-bid',
      part: 'Set what each frequency means',
      heading: 'Change a frequency',
      text: 'To change one, select Change, check and uncheck mealtimes, then select Save. Here BID becomes Breakfast and Dinner.\n\nSupplements residents already have are not changed when you save. Each order that arrives after that uses the new mealtimes.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/change-bid.webp',
          width: 1280,
          height: 466,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/change-bid.mp4',
          width: 1280,
          height: 466,
          durationSec: 6,
        },
        alt: 'BID being changed: Breakfast and Dinner checked, Lunch unchecked, with Cancel and Save.',
      },
    },
    {
      id: 'add',
      part: 'Set what each frequency means',
      heading: 'Add a frequency your EHR uses',
      text: 'If your EHR writes a frequency that is not listed, type it under Another frequency the way your EHR writes it, such as QAM, then select Add. Check its mealtimes and select Save.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/add.webp',
          width: 1280,
          height: 530,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/add.mp4',
          width: 1280,
          height: 530,
          durationSec: 6,
        },
        alt: 'QAM added under the other frequencies, with Breakfast checked.',
      },
    },
    {
      id: 'clear',
      part: 'Set what each frequency means',
      heading: 'Clear a frequency',
      text: 'Clear takes the mealtimes off a frequency. Dietech asks first: supplement text with it waits to be mapped again, and supplements already given are not changed.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/clear.webp',
          width: 708,
          height: 322,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/clear.mp4',
          width: 708,
          height: 322,
          durationSec: 2,
        },
        alt: 'The Clear MEALS? window: supplement text with MEALS will wait in Map this again until it is set. Supplements already given are not changed.',
      },
    },
    {
      id: 'supplement-mapping',
      part: 'How a supplement uses the frequency',
      heading: 'The supplement mapping decides',
      text: 'Under Mappings, select Supplements, then a mapping and Edit. With Mealtimes from the order\'s frequency checked, "Juven BID" is given at BID\'s mealtimes. The mealtimes checked below are used only when an order gives no frequency.\n\nUncheck it to give the supplement at the same mealtimes, whatever the order says.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/supplement-mapping.webp',
          width: 942,
          height: 1276,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/supplement-mapping.mp4',
          width: 942,
          height: 1276,
          durationSec: 2,
        },
        alt: 'The Edit Supplement Mapping window for JUVEN: the JUVEN global meal pattern, with Mealtimes from the order\'s frequency checked.',
      },
    },
    {
      id: 'supplement-given',
      part: 'How a supplement uses the frequency',
      heading: 'One item for each mealtime',
      text: 'On the resident, the supplement is added to the meal pattern at each of those mealtimes. On the EHR page, Added Items lists each one, such as JUVEN at Lunch and JUVEN at Dinner from the order "Juven BID".\n\nIf the global meal pattern has nothing at one of the mealtimes, it is not given there, and the mapping says so.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/supplement-given.webp',
          width: 1280,
          height: 568,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/supplement-given.mp4',
          width: 1280,
          height: 568,
          durationSec: 2,
        },
        alt: 'Added Items: JUVEN at Lunch and JUVEN at Dinner for Lindqvist, Bernard, both from the EHR order Juven BID.',
      },
    },
    {
      id: 'plans',
      part: 'Map an action plan',
      heading: 'Open the Action Plans list',
      text: 'Select Mappings, then Action Plans. Each mapping turns text your EHR sends, such as WEIGHT LOSS PROTOCOL, into a problem template from your plan of care. Message type says which part of the order the text comes in; Any part matches it wherever it comes.\n\nThe line above the list says whether action plans are turned on for your facility.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/plans.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/plans.mp4',
          width: 1280,
          height: 684,
          durationSec: 4.6,
        },
        alt: 'The Action Plans list: DYSPHAGIA maps to Swallowing difficulty, PRESSURE INJURY to Pressure injury, and WEIGHT LOSS PROTOCOL to Unintended weight loss. Above it: Action plans: On.',
      },
    },
    {
      id: 'new-plan',
      part: 'Map an action plan',
      heading: 'Add an action plan mapping',
      text: 'Select New. Type the EHR value exactly as your EHR sends it, keep Message type at Any part unless the text only matters in one part, and under Adds pick the Problem template.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/new-plan.webp',
          width: 942,
          height: 864,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/new-plan.mp4',
          width: 942,
          height: 864,
          durationSec: 6,
        },
        alt: 'The New Action Plan Mapping window: EHR value POOR PO INTAKE, Message type Any part, and the problem template Poor oral intake.',
      },
    },
    {
      id: 'save-plan',
      part: 'Map an action plan',
      heading: 'Save it',
      text: 'Select Save. The mapping is added to the list. From then on, an order with that text adds the plan to the resident, while action plans are turned on for your facility.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/save-plan.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/save-plan.mp4',
          width: 1280,
          height: 684,
          durationSec: 3.2,
        },
        alt: 'The Action Plans list now has POOR PO INTAKE, mapped to Poor oral intake.',
      },
    },
    {
      id: 'plan-added',
      part: 'What an action plan does for residents',
      heading: 'The plan is added to the resident',
      text: 'When an order has the text, Dietech copies the problem template into the resident\'s plan of care: the problem, its goal and its approaches, starting on the order\'s start date. Added Items lists it for review.\n\nIf the resident already has an open problem with the same title, nothing is added, so no plan is doubled.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/plan-added.webp',
          width: 1280,
          height: 568,
        },
        clip: {
          src: '/help/ehr-frequencies-action-plans/plan-added.mp4',
          width: 1280,
          height: 568,
          durationSec: 2,
        },
        alt: 'Added Items: Unintended weight loss, an action plan, added for Marsh, Theodore from the EHR order Weight loss protocol.',
      },
    },
    {
      id: 'plan-ended',
      part: 'What an action plan does for residents',
      heading: 'When the order stops',
      text: 'When the order stops, Dietech resolves the problem. It is never deleted, so it stays on the plan of care as resolved.\n\nIf staff changed the problem, Dietech leaves it open and lists it as Left open: please review. Look at it on the resident\'s plan of care and resolve it there if it is done. If the text comes back later, a new problem is added.',
      media: {
        image: {
          src: '/help/ehr-frequencies-action-plans/plan-ended.webp',
          width: 1280,
          height: 568,
        },
        alt: 'Added Items: Pressure injury for Vasquez, Irene, Left open: please review, because staff edited the action plan.',
      },
    },
  ],
  tips: [
    'Set every frequency your EHR uses before supplements are turned on, so no supplement text waits on one. The column Sent in the last 60 days shows which ones your EHR really sends.',
    'Write a frequency the way your EHR writes it. Dietech also understands common spellings, such as "twice daily" for BID.',
    'Before you map an action plan, check the problem template under Plan of Care, Configuration. The plan the resident gets is a copy of it.',
  ],
  notes: [
    'Dietech support turns supplements, tube feeds, global adjustments and meal patterns, and action plans on or off for your facility, each one on its own. While one is off, you can still make and check its mappings, but nothing is given to residents.',
    'Nothing is filled in for you. Until a frequency is set, supplement text with it waits in the Map this pop-up and stays a meal pattern note. It does not hold anything else in the message.',
    'Saving a frequency does not change supplements residents already have. Each order that arrives after that uses the new mealtimes.',
    'Changing frequencies and action plan mappings needs EHR level 2 and diet order mapping level 2 on your role. Ask your administrator.',
  ],
  related: ['ehr-supplements-tube-feeds', 'map-ehr-text', 'ehr-page', 'plan-of-care', 'meal-patterns'],
  recorded: '2026-11-03',
};

export default article;
